import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import { colors, fonts, spacing, borderRadius, shadows, tableStyles } from '../../constants';
import ProjectTableRow from './ProjectTableRow';

/**
 * ProjectsTable Component
 * Main table component with header and body
 */
const ProjectsTable = ({
  loading,
  totalProjects,
  projects,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  getStatusColor,
}) => {
  // Header cell style with no hover effects
  const headerCellStyle = {
    ...tableStyles.header,
    color: colors.textWhite,
    fontWeight: fonts.weightSemiBold,
    borderBottom: 'none !important',
    borderTop: 'none !important',
    borderLeft: 'none !important',
    borderRight: 'none !important',
    '&:hover': {
      borderBottom: 'none !important',
      borderTop: 'none !important',
      borderLeft: 'none !important',
      borderRight: 'none !important',
      backgroundColor: 'transparent !important',
      outline: 'none',
    },
    '&::before': {
      display: 'none',
    },
    '&::after': {
      display: 'none',
    },
  };

  return (
    <Paper sx={{ borderRadius: borderRadius.md, boxShadow: shadows.md, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: spacing.sm, sm: spacing.md },
          py: spacing.sm,
          background: '#1e293b',
          color: colors.textWhite,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: `${borderRadius.md} ${borderRadius.md} 0 0`,
          zIndex: 1,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: '#0e172a',
            filter: 'blur(50px)',
            opacity: 0.6,
            zIndex: 0,
          },
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: fonts.weightSemiBold,
            fontSize: { xs: fonts.fontSizeXs, sm: fonts.fontSizeSm },
            letterSpacing: '0.02em',
            position: 'relative',
            zIndex: 1,
          }}
        >
          Projects {totalProjects > 0 && `(${totalProjects})`}
        </Typography>
      </Box>
      <TableContainer 
        sx={{ 
          borderTop: `2px solid ${colors.borderDefault}`,
          maxHeight: 'calc(100vh - 350px)',
          overflow: 'auto',
        }}
      >
        <Table sx={{ minWidth: { xs: 800, sm: '100%' } }}>
          <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <TableRow 
              sx={{ 
                backgroundColor: '#1b2538',
                '&:hover': {
                  backgroundColor: '#1b2538',
                },
                '& .MuiTableCell-root': {
                  borderBottom: 'none !important',
                  borderTop: 'none !important',
                  borderLeft: 'none !important',
                  borderRight: 'none !important',
                  backgroundColor: '#1b2538',
                  '&:hover': {
                    borderBottom: 'none !important',
                    borderTop: 'none !important',
                    borderLeft: 'none !important',
                    borderRight: 'none !important',
                  },
                },
              }}
            >
              <TableCell sx={{ ...headerCellStyle, backgroundColor: '#1b2538' }}>
                Project Code
              </TableCell>
              <TableCell sx={{ ...headerCellStyle, backgroundColor: '#1b2538' }}>
                Project Name
              </TableCell>
              <TableCell sx={{ ...headerCellStyle, backgroundColor: '#1b2538' }}>
                Region
              </TableCell>
              <TableCell sx={{ ...headerCellStyle, backgroundColor: '#1b2538' }}>
                Description
              </TableCell>
              <TableCell sx={{ ...headerCellStyle, backgroundColor: '#1b2538' }}>
                Status
              </TableCell>
              <TableCell sx={{ ...headerCellStyle, backgroundColor: '#1b2538' }}>
                Start Date
              </TableCell>
              <TableCell sx={{ ...headerCellStyle, backgroundColor: '#1b2538' }}>
                End Date
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ ...tableStyles.cell, textAlign: 'center', py: spacing.lg }}>
                  <CircularProgress size={40} sx={{ color: colors.primary }} />
                </TableCell>
              </TableRow>
            ) : projects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ ...tableStyles.cell, textAlign: 'center', py: spacing.lg }}>
                  <Typography sx={{ color: colors.textSecondary, fontSize: fonts.fontSizeXs }}>
                    No projects found. Click "Add Project" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              projects.map((project) => (
                <ProjectTableRow
                  key={project.id}
                  project={project}
                  getStatusColor={getStatusColor}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalProjects}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          borderTop: `1px solid ${colors.borderDefault}`,
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: fonts.fontSizeXs,
            color: colors.textSecondary,
          },
          '& .MuiIconButton-root': {
            color: colors.textSecondary,
            '&:hover': {
              backgroundColor: colors.backgroundSoft,
            },
          },
        }}
      />
    </Paper>
  );
};

export default ProjectsTable;
